import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const Section = styled.section`
  margin-bottom: 16px;
  padding: 12px;
  background: ${(p) => p.theme.colors.surface.paper};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  }

  h2 {
    font-size: 16px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.neutral[900]};
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
      width: 22px;
      height: 22px;
      color: ${(p) => p.theme.colors.accent.main};
    }
  }
`;

const FollowUpBadge = styled.span`
  background: ${({ theme }) => theme.isDarkMode ? '#19fa6c' : theme.colors.primary.main};
  color: ${({ theme }) => theme.isDarkMode ? '#000' : '#fff'};
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  
  &.bounce {
    animation: badge-bounce 0.8s cubic-bezier(0.36, 0, 0.66, -0.56) forwards;
  }
  
  @keyframes badge-bounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.3); }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
  padding: 8px;
  background: ${(p) => p.theme.colors.surface.raised};
  border-radius: 12px;
`;

const Tag = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  background-color: ${(props) =>
    props.$active 
      ? props.theme.colors.accent.surface 
      : props.theme.isDarkMode 
        ? props.theme.colors.surface.raised 
        : "white"};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.accent.main
      : props.theme.isDarkMode
        ? '#fff'
        : props.theme.colors.neutral[700]};
  border-color: ${(props) =>
    props.$active
      ? props.theme.colors.accent.main
      : props.theme.isDarkMode
        ? props.theme.colors.neutral[700]
        : props.theme.colors.neutral[300]};

  &:hover {
    background-color: ${(props) =>
      props.$active
        ? props.theme.colors.accent.hover
        : props.theme.isDarkMode
          ? props.theme.colors.surface.default
          : props.theme.colors.surface.raised};
    color: ${(props) =>
      props.$active || props.theme.isDarkMode
        ? '#fff'
        : props.theme.colors.neutral[700]};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${(props) => props.theme.isDarkMode ? '#fff' : 'currentColor'};
  }
`;

const SuggestionsContainer = styled.div`
  display: grid;
  gap: 8px;
`;

const Suggestion = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: ${(p) => p.theme.colors.accent.main};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
    border-color: ${(p) => p.theme.colors.accent.main};

    &::before {
      opacity: 1;
    }
  }

  h3 {
    font-size: 12px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.neutral[900]};
    margin-bottom: 8px;
  }

  p {
    font-size: 10px;
    color: ${(p) => p.theme.colors.neutral[600]};
    line-height: 1.6;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: ${(p) => p.theme.colors.neutral[500]};
  font-style: italic;
`;

const AIAssistant = ({
  activeTags = [],
  onTagToggle,
  suggestions = [],
  onSuggestionClick,
  followUpCount = 0,
  animateFollowUp = false
}) => {
  const tags = [
    { id: "add-follow-up", label: "Add Follow Up", icon: "➕" },
    { id: "goals", label: "Goals & Objectives", icon: "🎯" },
    { id: "risk", label: "Risk Tolerance", icon: "📊" },
    { id: "income", label: "Income & Assets", icon: "💰" },
    { id: "expenses", label: "Expenses", icon: "💳" },
    { id: "family", label: "Family Situation", icon: "👨‍👩‍👧‍👦" },
    { id: "health", label: "Health & Insurance", icon: "🏥" },
    { id: "estate", label: "Estate Planning", icon: "📝" },
    { id: "tax", label: "Tax Planning", icon: "📋" },
  ];

  console.log("🛰️ [AIAssistant] render:", { followUpCount, animateFollowUp });

  return (
    <Section>
      <h2>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
        </svg>
        AI Assistant
      </h2>

      <TagsContainer>
        {tags.map((tag) => (
          <Tag
            key={tag.id}
            $active={activeTags.includes(tag.id)}
            onClick={() => onTagToggle(tag.id)}
          >
            {tag.icon} {tag.label}
            {tag.id === "add-follow-up" && followUpCount > 0 && (
              <FollowUpBadge className={animateFollowUp ? "bounce" : ""}>
                +{followUpCount}
              </FollowUpBadge>
            )}
          </Tag>
        ))}
      </TagsContainer>

      <SuggestionsContainer>
        {suggestions.length > 0 ? (
          suggestions.map((suggestion, index) => (
            <Suggestion
              key={index}
              onClick={() => onSuggestionClick(suggestion)}
            >
              <h3>{suggestion.title}</h3>
              <p>{suggestion.description}</p>
            </Suggestion>
          ))
        ) : (
          <EmptyState>
            No suggestions available. Try selecting different tags to get
            personalized recommendations.
          </EmptyState>
        )}
      </SuggestionsContainer>
    </Section>
  );
};

AIAssistant.propTypes = {
  activeTags: PropTypes.arrayOf(PropTypes.string),
  onTagToggle: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ),
  onSuggestionClick: PropTypes.func.isRequired,
  followUpCount: PropTypes.number,
  animateFollowUp: PropTypes.bool
};

export default AIAssistant;
